import { Box, Paper, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { feeStructurePrerequisite } from "../../shared/casePrerequisites";
import { Case, RevenueData, StageRevenue } from "../../shared/CaseModel";
import CenteredMessageBox from "../general/CenteredMessageBox";
import {
  signedTone,
  TABULAR_NUMERALS,
  toneColors,
} from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { SplitBar } from "../general/SplitBar";
import { PrerequisiteChecklist } from "./PrerequisiteChecklist";
import { RevenueBarChart } from "./RevenueBarChart";
import { RevenuePart, revenueColors, revenueSplit } from "./revenueParts";

const sumField = <K extends string>(data: Record<K, number>[], field: K) =>
  data.reduce((sum, d) => sum + d[field], 0);

const NO_REVENUE: RevenueData = {
  incurredRevenue: [],
  estimatedRevenue: [],
  incurredTotal: 0,
  estimatedTotal: 0,
  cfaTotal: 0,
  cfaExpenses: 0,
  total: 0,
};

export const RevenueAnalytics = () => {
  const caseContext = useCase();
  const c: Case = caseContext.getCase();

  // Revenue is the one view that also needs the fee structure — it is what
  // splits client fees into costs and profit. AnalyticsGate deliberately leaves
  // it out so every other view renders without it; the row is appended here so
  // the reader sees one checklist rather than a different kind of message.
  const feeStructure = c.costStructure?.clientFeeStructure;
  if (!feeStructure) {
    return (
      <PrerequisiteChecklist
        caseId={c.id}
        prerequisites={[
          ...caseContext.getPrerequisites(),
          feeStructurePrerequisite(c),
        ]}
      />
    );
  }

  // Precomputed server-side (see calculateResults) and stored, so we don't read
  // per-outcome financialOutcomes here — those details are stripped from results.
  return <Revenue data={c.results?.revenueData ?? NO_REVENUE} />;
};

/**
 * A revenue of the firm as a card: what it comes to and what it consists of
 * as figures, the same as one bar where the parts are shares of the whole,
 * and, for the stages it is made in, as a chart.
 */
const RevenueCard = ({
  title,
  total,
  amounts,
  profit,
  byStage,
}: {
  title: string;
  total: number;
  amounts: { [part in RevenuePart]: number };
  // what is left of the revenue for the firm; a loss where it is negative
  profit: number;
  byStage?: { title: string; stages: StageRevenue[] };
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.revenue",
  });
  const colors = revenueColors(theme);
  const tones = toneColors(theme);
  const labels: { [part in RevenuePart]: string } = {
    indirectCosts: t("indirectCosts"),
    directCosts: t("directCosts"),
    cfaFees: t("cfa.fees"),
    margin: t("margin"),
  };
  const split = revenueSplit(total, amounts);

  const figure = (
    label: string,
    amount: number,
    swatch?: string,
    color?: string,
  ): ReactNode => (
    <div key={label}>
      <Typography
        variant="caption"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.875,
          fontWeight: 500,
          color: "text.secondary",
        }}
      >
        {swatch && (
          <Box
            component="span"
            sx={{
              flex: "none",
              width: 10,
              height: 10,
              borderRadius: 0.75,
              bgcolor: swatch,
            }}
          />
        )}
        {label}
      </Typography>
      <Typography
        variant="h5"
        component="p"
        sx={{
          fontWeight: 700,
          letterSpacing: "-.015em",
          lineHeight: 1.25,
          color,
          ...TABULAR_NUMERALS,
        }}
      >
        {currency.format(amount)}
      </Typography>
    </div>
  );

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2.25 }}
    >
      <SectionLabel component="h2">{title}</SectionLabel>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          columnGap: 3.25,
          rowGap: 2,
          mt: 1.75,
        }}
      >
        {figure(t("total"), total)}
        {figure(
          labels.indirectCosts,
          amounts.indirectCosts,
          colors.indirectCosts.color,
        )}
        {figure(
          labels.directCosts,
          amounts.directCosts,
          colors.directCosts.color,
        )}
        {/* a revenue without a conditional fee agreement has no such fees */}
        {amounts.cfaFees !== 0 &&
          figure(labels.cfaFees, amounts.cfaFees, colors.cfaFees.color)}
        {figure(
          profit < 0 ? t("loss") : t("margin"),
          profit,
          colors.margin.color,
          profit === 0 ? undefined : tones[signedTone(profit)],
        )}
      </Box>

      {split && (
        <Box sx={{ mt: 1.75 }}>
          <SplitBar
            parts={split.map(({ part, share, percent }) => ({
              key: part,
              label: labels[part],
              share,
              shareText: `${percent}%`,
              ...colors[part],
            }))}
          />
        </Box>
      )}

      {byStage && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5 }}>
            {byStage.title}
          </Typography>
          <RevenueBarChart data={byStage.stages} currency={currency} />
        </>
      )}
    </Paper>
  );
};

/**
 * What the firm earns from a case: in all, from the work that is done, and
 * from the work that is still to come.
 */
export const Revenue = ({ data }: { data: RevenueData }) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.revenue",
  });
  const {
    incurredRevenue,
    estimatedRevenue,
    incurredTotal,
    estimatedTotal,
    cfaTotal,
    cfaExpenses,
    total,
  } = data;

  const hasRevenue = total > 0 || cfaTotal !== 0;

  const incurred = {
    indirectCosts: sumField(incurredRevenue, "indirectCosts"),
    directCosts: sumField(incurredRevenue, "directCosts"),
    margin: sumField(incurredRevenue, "margin"),
  };
  const estimated = {
    indirectCosts: sumField(estimatedRevenue, "indirectCosts"),
    directCosts: sumField(estimatedRevenue, "directCosts"),
    margin: sumField(estimatedRevenue, "margin"),
  };

  return (
    <Stack spacing={2.25} sx={{ userSelect: "none" }}>
      <Box>
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
        >
          {t("title")}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
        >
          {t("intro")}
        </Typography>
      </Box>

      {!hasRevenue && <CenteredMessageBox message={t("none")} />}

      {hasRevenue && (
        <>
          <RevenueCard
            title={t("description")}
            total={total}
            amounts={{
              indirectCosts: incurred.indirectCosts + estimated.indirectCosts,
              directCosts: incurred.directCosts + estimated.directCosts,
              cfaFees: cfaTotal,
              margin: incurred.margin + estimated.margin,
            }}
            profit={incurred.margin + estimated.margin + cfaTotal - cfaExpenses}
          />
          <RevenueCard
            title={t("incurred.title")}
            total={incurredTotal}
            // the fees of a conditional fee agreement are still to come
            amounts={{ ...incurred, cfaFees: 0 }}
            profit={incurred.margin}
            byStage={{ title: t("incurred.byStage"), stages: incurredRevenue }}
          />
          <RevenueCard
            title={t("estimated.title")}
            total={estimatedTotal + cfaTotal}
            amounts={{ ...estimated, cfaFees: cfaTotal }}
            profit={estimated.margin + cfaTotal - cfaExpenses}
            byStage={{
              title: t("estimated.byStage"),
              stages: estimatedRevenue,
            }}
          />
        </>
      )}
    </Stack>
  );
};
